/**
 * International phone number normalizer (no external dependencies).
 * Replaces the US-only normalizePhoneNumberUS.js to support all countries
 * in the Stripe payout details form.
 *
 * Accepts phone numbers with international prefix like:
 *   +49 176 12345678  (German mobile)
 *   +1 202 555 0102   (US)
 *   +358 55 1234567   (Finnish)
 *   +33 6 12 34 56 78 (French)
 */

// Minimum length of a valid international phone number (E.164 min is 8 digits)
const MIN_PHONE_DIGITS = 7;
// Maximum length of a valid international phone number (E.164 max is 15 digits)
const MAX_PHONE_DIGITS = 15;

/**
 * Format a phone number for display.
 * Used by react-final-form as the `format` prop on the field.
 *
 * Simply passes through the stored value for display.
 * This preserves the '+' prefix and any spaces the user typed.
 *
 * @param {String} value - the stored phone number
 * @returns {String} the phone number for display
 */
export const format = value => {
    if (!value) return '';
    return value;
};

/**
 * Parse a phone number from user input into a storable format.
 * Used by react-final-form as the `parse` prop on the field.
 *
 * Keeps only digits, the leading '+', and spaces.
 * This allows users to type international numbers like +49 176 12345678.
 *
 * @param {String} value - the displayed phone number
 * @returns {String} cleaned phone number
 */
export const parse = value => {
    if (!value) return value;

    // Allow: leading '+', digits, and spaces
    const hasPlus = value.charAt(0) === '+';
    // Remove everything except digits and spaces
    const cleaned = value.replace(/[^\d\s]/g, '');
    return hasPlus ? '+' + cleaned : cleaned;
};

/**
 * Validate a phone number.
 * Checks that the number starts with '+' and has a valid number of digits.
 *
 * @param {String} message - error message to return if invalid
 * @returns {Function} validator function for react-final-form
 */
export const validatePhoneNumber = message => value => {
    if (!value) return message;

    // Must start with '+'
    if (value.charAt(0) !== '+') {
          return message;
    }

    // Count digits only
    const digits = value.replace(/[^\d]/g, '');

    // Check digit count is within valid range
    if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
          return message;
    }

    return undefined; // valid
};
